import React from 'react';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import type { LDFlagSet } from 'launchdarkly-js-client-sdk';
import useUserInfo from '../../auth/useUserInfo';
import { environmentConfig } from '../../lib/environment-config';

export type LaunchDarklyFlags =
  | undefined
  | Partial<{
      SHOW_TIME_BLOCK: boolean;
      isAutoBlocksEnabled: boolean;
      'hide-build-ui-elements': boolean;
      'enable-templates-access': boolean;
      'add-automatic-time-stamp-signatures-to-user-preferences': boolean;
      'enable-broker-management-link': boolean;
      'show-edit-envelope-name': boolean;
      'hide-monthly-plan': boolean;
      'stripe-price-increase-enabled': boolean;
      'show-smart-email': boolean;
      'forms-warning-modal': boolean;
      'enable-date-stamping-preferences': boolean;
      'template-group-sharing': boolean;
      'template-office-sharing': boolean;
      'enable-template-sharing': boolean;
      'sign-now': boolean;
      'enable-email-settings-preference': boolean;
      'subscription-required': boolean;
      'automated-reminders': boolean;
      'is-email-length-limit-enabled': boolean;
      envelopeSentRedesign: boolean;
      enableEnvelopeBuilderTemplatePicker: boolean;
      sellersShieldEnvelopeSentBanner: boolean;
      inPersonSigning: boolean;
    }>;

type SetFlags = (flags: LDFlagSet) => void;
type LDUserInfo = {
  name: string;
  email: string;
  accountId: string;
  ds3UserId: string;
  subscriberId: string;
  primeContactType: string;
};

async function initializeLD(setFlags: SetFlags, userInfo: LDUserInfo | null) {
  if (process.env.JEST_WORKER_ID) {
    setFlags({});
    return;
  }

  const client = LaunchDarkly.initialize(
    environmentConfig.LAUNCH_DARKLY_CLIENT_ID,
    userInfo
      ? {
          name: userInfo.name,
          key: userInfo.ds3UserId,
          email: userInfo.email,
          custom: {
            accountId: userInfo.accountId,
            subscriberId: userInfo.subscriberId,
            primeContactType: userInfo.primeContactType,
          },
        }
      : { key: 'logged-out-user' }
  );
  client.on('ready', () => {
    setFlags(client.allFlags());
  });
}

type LaunchDarklyState = { flags: LaunchDarklyFlags; loading: boolean };
const defaultValue: LaunchDarklyState = { flags: {}, loading: true };
export const LaunchDarklyContext = React.createContext(defaultValue);

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const LaunchDarklyProvider = ({ children }: { children: any }) => {
  const [value, setValue] = React.useState(defaultValue);
  const initSuccessRef = React.useRef(false);

  const userInfo = useUserInfo();

  React.useEffect(() => {
    if (userInfo) {
      initSuccessRef.current = true;
      initializeLD(
        (flags) => {
          setValue({ flags, loading: false });
        },
        {
          name: userInfo.senderFirstName + ' ' + userInfo.senderLastName,
          email: userInfo.senderEmail,
          ds3UserId: userInfo.senderId,
          accountId: userInfo.digisignAccountId,
          subscriberId: userInfo.subscriberId || 'NoSubscriberId',
          primeContactType: userInfo.primeContactType || 'NoPrimeContactType',
        }
      );
    } else {
      // Ideally this case would only run if the user was truly logged out, but unfortunately
      // there isn't a good way to distinguish between "logged out" and "still loading user info".
      // On the first render `userAuth().authState` is defined and `userAuth().authState.isAuthenticated` is false,
      // which is identical to how it looks if the user is actually logged out.

      initializeLD((flags) => {
        // This avoids a race condition where THIS initializeLD() CALLBACK fires AFTER
        // the one above even though this initializeLD() was originally invoked first
        if (!initSuccessRef.current) setValue({ flags, loading: false });
      }, null);
    }
  }, [userInfo]);
  return <LaunchDarklyContext.Provider value={value}>{children}</LaunchDarklyContext.Provider>;
};

export function withLaunchDarkly<P extends object>(Comp: React.ComponentType<P>) {
  return (props: Omit<P, 'flags'>): any => {
    // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
    const render = ({ flags, loading: loadingLaunchDarklyFlags }: LaunchDarklyState) => {
      if (loadingLaunchDarklyFlags) {
        return null;
      }
      return <Comp {...(props as any)} flags={flags} />;
    };
    return <LaunchDarklyContext.Consumer>{render}</LaunchDarklyContext.Consumer>;
  };
}
